/**
 * Bundle 94 - two small admin tiles rendered near the top of the
 * Data Health page:
 *   1. "Privacy Headers (Production)" - server-side fetch of the 4
 *      production URLs; shows a per-URL pass/fail pill for
 *      X-Robots-Tag, Referrer-Policy, Cache-Control.
 *   2. "Stripe Mode" - reports whether the backend key is test /
 *      live / unconfigured. Never exposes the key.
 *
 * No em/en dashes. No `eslint-disable`. No `photo`/`image` in alt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, Shield, CreditCard } from 'lucide-react';
import api from '@/utils/api';


const Pill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
    }`}
    data-testid={`pill-${label.replace(/\s+/g, '-').toLowerCase()}-${ok ? 'ok' : 'miss'}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
    {label}
  </span>
);


const _has = (v, needle) => (v || '').toLowerCase().includes(needle);


const PrivacyHeadersTile = () => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get('/admin/health/privacy-headers');
      setData(r);
    } catch (e) {
      setData({ urls: [], all_ok: false, error: e?.response?.data?.detail || 'Failed to load.' });
    }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="tile-privacy-headers">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary-700" aria-hidden="true" />
            <div className="font-display font-semibold text-primary-900">Privacy Headers (Production)</div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="privacy-headers-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {data?.error ? (
          <div className="text-xs text-red-700" data-testid="privacy-headers-error">{data.error}</div>
        ) : null}
        <div className="space-y-2 text-xs">
          {(data?.urls || []).map((u) => (
            <div key={u.url} className="flex flex-col gap-1" data-testid={`privacy-row-${u.url.replace(/https:\/\/dostination\.com/, '').replace(/\W+/g, '-')}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-primary-800">{u.url.replace('https://dostination.com', '')}</span>
                <span className="text-neutral-500">{u.status_code ?? 'ERR'}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                <Pill ok={_has(u.x_robots_tag, 'noindex')} label="X-Robots-Tag" />
                <Pill ok={_has(u.referrer_policy, 'no-referrer')} label="Referrer-Policy" />
                <Pill ok={_has(u.cache_control, 'no-store')} label="Cache-Control" />
              </div>
            </div>
          ))}
        </div>
        {data && !data.error ? (
          <div className={`mt-3 text-[11px] ${data.all_ok ? 'text-green-800' : 'text-amber-800'}`} data-testid="privacy-headers-summary">
            {data.all_ok
              ? 'All 4 production routes stamped correctly.'
              : 'One or more routes are missing a header. Trigger a redeploy and Refresh.'}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};


const StripeModeTile = () => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get('/admin/health/stripe-mode');
      setData(r);
    } catch (e) {
      setData({ mode: 'unknown', error: e?.response?.data?.detail || 'Failed.' });
    }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const modeStyle = {
    test: 'bg-amber-50 text-amber-800',
    live: 'bg-green-50 text-green-800',
    unconfigured: 'bg-red-50 text-red-800',
    unknown: 'bg-neutral-100 text-neutral-700',
  }[data?.mode || 'unknown'];

  return (
    <Card data-testid="tile-stripe-mode">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary-700" aria-hidden="true" />
            <div className="font-display font-semibold text-primary-900">Stripe Mode</div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="stripe-mode-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${modeStyle}`} data-testid="stripe-mode-pill">
            {data?.mode || 'unknown'}
          </span>
          {data?.account_country ? (
            <span className="text-xs text-neutral-500">country: {data.account_country}</span>
          ) : null}
          {typeof data?.charges_enabled === 'boolean' ? (
            <span className="text-xs text-neutral-500" data-testid="stripe-charges-enabled">
              charges: {data.charges_enabled ? 'yes' : 'no'}
            </span>
          ) : null}
          {typeof data?.payouts_enabled === 'boolean' ? (
            <span className="text-xs text-neutral-500">payouts: {data.payouts_enabled ? 'yes' : 'no'}</span>
          ) : null}
        </div>
        {data?.error ? (
          <div className="text-[11px] text-red-700 mt-2" data-testid="stripe-mode-error">{data.error}</div>
        ) : null}
        {data && !data.error ? (
          <div className="text-[11px] text-neutral-500 mt-2">
            Capabilities: {Object.entries(data.capabilities_summary || {}).map(([k, v]) => `${v} ${k}`).join(', ') || 'none reported'}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};


export const PrivacyStripeHealthTiles = () => (
  <div className="grid gap-3 md:grid-cols-2 mb-6" data-testid="privacy-stripe-tiles">
    <PrivacyHeadersTile />
    <StripeModeTile />
  </div>
);


export default PrivacyStripeHealthTiles;
